import { useEffect, useState } from "react"

/**
 * One source of truth for viewport questions.
 *
 * These match Tailwind's defaults on purpose: a component that branches in JS
 * and a sibling that branches with `md:` have to agree, or the layout changes
 * at two different widths and nobody can tell which rule won.
 */
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
} as const

export type Breakpoint = keyof typeof BREAKPOINTS

/**
 * True while the viewport is at least `breakpoint` wide.
 *
 * Returns false during SSR and on the very first client render, so callers
 * should treat "false" as "not yet known to be wide" — i.e. render the mobile
 * form first. That is the safe direction: a phone never briefly renders the
 * desktop layout it cannot fit.
 */
export function useBreakpoint(breakpoint: Breakpoint) {
  const query = `(min-width: ${BREAKPOINTS[breakpoint]}px)`
  const [isAbove, setIsAbove] = useState(false)

  useEffect(() => {
    const mql = window.matchMedia(query)
    const onChange = () => setIsAbove(mql.matches)
    onChange()
    mql.addEventListener("change", onChange)
    return () => mql.removeEventListener("change", onChange)
  }, [query])

  return isAbove
}

/**
 * The one below-`md` question the app asks constantly.
 *
 * This was `useIsMobile` in `use-mobile.ts`, with its own hardcoded 768 and
 * its own listener. Same behaviour, one definition — the hook is kept under
 * its old name because the sidebar and nav-user read as "is this a phone",
 * not "is this narrower than md".
 */
export function useIsMobile() {
  return !useBreakpoint("md")
}
